
import React from 'react';
import { motion } from 'framer-motion';
import { PROJECTS, TECH_STACK } from '../constants';
import { ExternalLink, ArrowUpRight, Cpu, Layers, Activity } from 'lucide-react';

interface BentoGridProps {
  hideHeader?: boolean;
}

const BentoGrid: React.FC<BentoGridProps> = ({ hideHeader = false }) => {
  // Componente reutilizable para tarjetas de proyecto uniformes
  const ProjectCard = ({ project }: { project: typeof PROJECTS[0] }) => (
    <motion.article 
      whileHover={{ y: -5 }}
      className="md:col-span-2 h-[300px] md:h-[350px] group relative overflow-hidden glass-panel rounded-xl neon-border-hover cursor-pointer border border-white/5"
    >
      <a 
        href={project.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full h-full"
        aria-label={`Ver proyecto: ${project.title}`}
      >
        <img 
          src={project.image} 
          alt={`Proyecto de desarrollo web para ${project.title}`} 
          className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" 
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/40 to-transparent p-6 md:p-10 flex flex-col justify-end">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-[1px] bg-[#39FF14]/60" />
            <span className="text-[#39FF14] text-[10px] md:text-xs font-bold uppercase tracking-[0.2em]">{project.category}</span>
          </div>
          <h3 className="text-2xl md:text-4xl font-bold mb-4 font-syncopate uppercase tracking-tighter leading-tight">{project.title}</h3>
          <p className="text-gray-400 text-xs md:text-sm max-w-md mb-6 line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            {project.description}
          </p>
          <div className="flex flex-wrap gap-2">
            {project.tech.map(t => (
              <span key={t} className="text-[9px] md:text-[10px] bg-white/10 px-3 py-1 rounded-sm uppercase tracking-wider font-bold border border-white/5">
                {t}
              </span>
            ))}
          </div>
          <div className="absolute top-8 right-8 w-12 h-12 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
            <ArrowUpRight className="text-[#39FF14]" size={24} />
          </div>
        </div>
      </a>
    </motion.article>
  );

  return (
    <section id="projects" className={`${hideHeader ? 'py-0' : 'py-16 md:py-24'} px-6 lg:px-20 max-w-7xl mx-auto`} aria-label="Portfolio de Proyectos">
      {!hideHeader && (
        <div className="mb-12 md:mb-20">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-[1px] w-12 bg-[#39FF14]/40" />
            <span className="text-[10px] md:text-xs font-bold tracking-[0.4em] uppercase text-[#39FF14] neon-glow">Proyectos Seleccionados</span>
          </div>
          <h2 className="font-syncopate text-3xl md:text-5xl font-bold tracking-tight uppercase">
            CASOS DE <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#39FF14] to-emerald-400">ÉXITO</span>
          </h2>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Fila 1 de Proyectos */}
        <ProjectCard project={PROJECTS[0]} />
        <ProjectCard project={PROJECTS[1]} />

        {/* Fila 2 de Proyectos */}
        <ProjectCard project={PROJECTS[2]} />
        <ProjectCard project={PROJECTS[3]} />

        {/* Bloques Informativos Diferenciados (Fila de Utilidad) */}
        
        {/* Bloque Tecnologías - Estilo "Data Terminal" */}
        <article className="md:col-span-1 glass-panel rounded-xl p-6 md:p-8 border-t-2 border-t-[#39FF14] flex flex-col justify-between min-h-[220px]">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Layers className="text-[#39FF14]" size={20} />
              <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#39FF14]">Tech Matrix</h3>
            </div>
            <p className="text-gray-400 text-[10px] md:text-xs uppercase tracking-tight leading-relaxed mb-4">
              Implementación de arquitecturas reactivas y escalables.
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {TECH_STACK.slice(0, 4).map(t => (
              <span key={t} className="px-2 py-0.5 bg-[#39FF14]/5 border border-[#39FF14]/10 text-[#39FF14] text-[8px] font-bold rounded uppercase">
                {t}
              </span>
            ))}
          </div>
        </article>

        {/* Bloque Experiencia - Estilo "Status Dashboard" */}
        <div className="md:col-span-2 glass-panel rounded-xl p-6 md:p-8 flex items-center justify-between border border-white/5 relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#39FF14]/30 to-transparent" />
          <div className="flex flex-col gap-1">
            <span className="text-4xl md:text-6xl font-bold text-[#39FF14] font-syncopate leading-none tracking-tighter">04+</span>
            <span className="text-[10px] uppercase font-bold tracking-[0.3em] text-gray-500">Deployments Ready</span>
          </div>
          <div className="w-[1px] h-12 bg-white/10 mx-6 hidden sm:block" />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 text-white/80">
              <Activity size={14} className="text-[#39FF14] animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-widest">Core Web Vitals</span>
            </div>
            <div className="w-full h-1 bg-white/5 rounded-full mb-1">
              <div className="w-[98%] h-full bg-[#39FF14] shadow-[0_0_8px_#39FF14]" />
            </div>
            <p className="text-[9px] text-gray-500 uppercase font-bold tracking-widest">Performance Optimization: 98% AVG</p>
          </div>
        </div>

        {/* Bloque Enfoque - Estilo "System Core" */}
        <div className="md:col-span-1 glass-panel rounded-xl p-6 md:p-8 border-l-2 border-l-[#39FF14] bg-gradient-to-br from-[#39FF14]/5 to-transparent">
          <div className="flex items-center gap-2 mb-4">
            <Cpu className="text-[#39FF14]" size={20} />
            <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#39FF14]">System Core</h4>
          </div>
          <p className="text-[10px] md:text-xs font-medium text-gray-300 leading-relaxed uppercase tracking-tight">
            Diseño <span className="text-[#39FF14]">Conversion-Ready</span> para dominar nichos competitivos.
          </p>
          <div className="mt-6 flex gap-1">
            {[1, 2, 3].map(i => (
              <div key={i} className={`w-1 h-1 rounded-full bg-[#39FF14] animate-pulse delay-${i * 150}`} />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default BentoGrid;
